import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { budgetApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import MoneyInput, { PercentInput } from "../../components/MoneyInput";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

type PaymentFormData = {
  item: string;
  quantity: string;
  cost: string;
  discount: string;
  discount_type: "amount" | "percent";
  cost_type: "charge" | "expense";
  gst: "inclusive" | "exclusive" | "free";
};

const PaymentForm = ({ handleCloseForm, projectId, onSuccess, payment }: any) => {
  const [formData, setFormData] = useState<PaymentFormData>({
    item: "",
    quantity: "1",
    cost: "",
    discount: "0",
    discount_type: "amount",
    // What this is for the job's margin, and whether it reaches an invoice.
    cost_type: "charge",
    // One choice covering both questions Xero asks: whether the figure already
    // contains GST, and whether GST applies at all.
    gst: "inclusive",
  });

  const editing = Boolean(payment?.payment_id);

  useEffect(() => {
    if (!payment) return;
    setFormData({
      item: payment.item || "",
      quantity: String(payment.quantity ?? "1"),
      cost: String(payment.cost ?? ""),
      discount: String(payment.discount ?? "0"),
      discount_type: payment.discount_type || "amount",
      cost_type: payment.cost_type || "charge",
      gst: !payment.gst_applies
        ? "free"
        : payment.gst_inclusive
          ? "inclusive"
          : "exclusive",
    });
  }, [payment]);

  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    const { item, quantity, cost, gst, cost_type, discount, discount_type } =
      formData;

    const insertForm = {
      project_id: projectId,
      item,
      quantity: quantity || "1",
      cost,
      cost_type,
      discount: discount || "0",
      discount_type,
      gst_inclusive: gst === "inclusive",
      gst_applies: gst !== "free",
      // Whether this has been paid is Xero's answer, arriving through the
      // invoice it is billed on. Recording it here as well would give two
      // sources that drift.
      payment_status: "Outstanding" as const,
      paid_on: null,
    };

    const { error } = editing
      ? await budgetApi.updatePayment(payment.payment_id, {
          item,
          quantity: quantity || "1",
          cost,
              cost_type,
          discount: discount || "0",
          discount_type,
          gst_inclusive: gst === "inclusive",
          gst_applies: gst !== "free",
        })
      : await budgetApi.createPayment(projectId, insertForm);
    setIsLoading(false);

    if (error) {
      setErrorMessage(`Failed to create payment: ${error.message}`);
      return;
    }

    setSuccessMessage("Payment added successfully.");
    if (typeof onSuccess === "function") {
      await onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <DialogHeader>
        <DialogTitle>{editing ? "Edit cost" : "Add cost"}</DialogTitle>
        <DialogDescription>
          Record a cost on this project. Invoice it below when you are ready; payment status comes from Xero.
        </DialogDescription>
      </DialogHeader>

      {errorMessage && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="rounded-md border border-tone-success-border bg-tone-success-surface px-3 py-2 text-sm text-tone-success-foreground">
          {successMessage}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-6">
        <div className="space-y-2 sm:col-span-6">
          <Label htmlFor="payment-item">Item</Label>
          <Input
            id="payment-item"
            name="item"
            value={formData.item}
            onChange={handleChange}
            required
            placeholder="Packing service, cleaning service"
          />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="payment-quantity">Quantity</Label>
          <Input
            id="payment-quantity"
            name="quantity"
            inputMode="decimal"
            value={formData.quantity}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                quantity: event.target.value.replace(/[^0-9.]/g, ""),
              }))
            }
            required
          />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="payment-cost">Unit cost</Label>
          <MoneyInput
            id="payment-cost"
            name="cost"
            value={formData.cost}
            onChange={(next) =>
              setFormData((previous) => ({ ...previous, cost: String(next) }))
            }
            required
          />
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label>GST</Label>
          <Select
            value={formData.gst}
            onValueChange={(value) =>
              setFormData((previous) => ({
                ...previous,
                gst: value as PaymentFormData["gst"],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="inclusive">GST inclusive</SelectItem>
              <SelectItem value="exclusive">GST exclusive</SelectItem>
              <SelectItem value="free">GST free</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 sm:col-span-3">
          <Label htmlFor="payment-discount">Discount</Label>
          <div className="grid grid-cols-[1fr_130px] gap-2">
            {formData.discount_type === "percent" ? (
              <PercentInput
                id="payment-discount"
                name="discount"
                value={formData.discount}
                onChange={(next) =>
                  setFormData((previous) => ({
                    ...previous,
                    discount: String(next),
                  }))
                }
              />
            ) : (
              <MoneyInput
                id="payment-discount"
                name="discount"
                value={formData.discount}
                onChange={(next) =>
                  setFormData((previous) => ({
                    ...previous,
                    discount: String(next),
                  }))
                }
              />
            )}
            <Select
              value={formData.discount_type}
              onValueChange={(value) =>
                setFormData((previous) => ({
                  ...previous,
                  discount_type: value as PaymentFormData["discount_type"],
                }))
              }
            >
              <SelectTrigger aria-label="Discount type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="amount">$ off</SelectItem>
                <SelectItem value="percent">% off</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2 sm:col-span-3">
          <Label>Type</Label>
          <Select
            value={formData.cost_type}
            onValueChange={(value) =>
              setFormData((previous) => ({
                ...previous,
                cost_type: value as PaymentFormData["cost_type"],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="charge">Charge to client</SelectItem>
              <SelectItem value="expense">Expense we incur</SelectItem>
            </SelectContent>
          </Select>
        </div>

      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={handleCloseForm}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {editing ? "Save" : "Add"}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default PaymentForm;
